import { html, LitElement, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { DebugInfo } from './elements/debug-info';
import { ServerClock } from './elements/server-clock';
import { KryptonElement } from './elements/krypton-element';
import { ENVIRONMENT, DEFAULT_VERSION } from './elements/app-version';
import { LoadingCurtain } from './elements/loading-curtain';
import { ContextMenu, MenuContext, MenuContextJsonData, WorkspaceJsonData, WorkspaceMenu } from './elements/menu-context';
import { Menu, MenuItem, MenuItemJsonData, MenuJson } from './elements/menu-item';
import { NotificationElement } from './elements/notification-element';
import { NotificationType } from './modules/notification';
import { PageElement } from './elements/page-element';
import { PageManager } from './elements/page-manager';
import { PageNav } from './elements/page-nav';
import { ICONS } from './modules/resources';
import { UrlResolver } from './modules/urlResolver';
import { openModule, openOverlay } from './modules/global_function';
import { KryptonConstants } from './modules/krypton_constants';
import { KryptonEventDetail } from './elements/krypton-event-detail';
import { KryptonDialog } from './elements/krypton-dialog'
import './elements/notifcation-board';
import { CSRF } from "./modules/CSRF";
import { KryptonAppStyles } from './krypton-app-styles';

export class KryptonApp extends LitElement {
    static url = {
        menu: "/WebDispatch/menu/view",
        message: "/resources/data/messages.json",
        help: "/WebDispatch/menu/help"
    };
    static data = {
        menu: null,
        help: null,
        copyrightDate: {
            start: 1998,
            end: ServerClock.clock.getDateTime().json().year
        }
    };

    // Some scripts can be executed only after page is shown.  Th property postShowPageHandlerRegistry is an array of such functions.  After page is shown,
    // they will get exeucuted one by one
    private postShowPageHandlerRegistry: any

    @property({ type: String }) accessor version: string = DEFAULT_VERSION;
    @property({ type: String }) accessor environment: ENVIRONMENT = "local";
    @property({ type: String }) accessor branch: string = "";
    @property({ type: String }) accessor debugPage: string = "no";

    private prevScrollTop: number = 0;
    private _disabled: boolean = false;
    get disabled() {
        return this._disabled;
    }

    @property({ type: Boolean, reflect: true })
    set disabled(disabled) {
        const oldValue = this._disabled;
        this._disabled = disabled;
        this.requestUpdate("disabled", oldValue);
    }

    private _pageManager: PageManager;
    get pageManager(): PageManager {
        if (!this._pageManager) this._pageManager = (this.shadowRoot?.getElementById("page-manager") || document.getElementById("page-manager")) as PageManager;
        return this._pageManager;
    }

    private _nav: PageNav;
    get nav(): PageNav {
        if (!this._nav) this._nav = (this.shadowRoot?.getElementById("nav") || document.getElementById("nav")) as PageNav;
        return this._nav;
    }

    private _loadingCurtain: LoadingCurtain;
    get loadingCurtain(): LoadingCurtain {
        if (!this._loadingCurtain) this._loadingCurtain = (this.shadowRoot?.getElementById("loading-curtain") || document.getElementById("loading-curtain")) as LoadingCurtain;
        return this._loadingCurtain;
    }

    private _loadingPage: PageElement | undefined = undefined;
    get loadingPage(): PageElement | undefined {
        return this._loadingPage;
    }

    get action() {
        return this.pageManager.lastAction;
    }

    constructor() {
        super();
        if (window["responsive"]?.is?.mobile?.any) {
            this.classList.add("clearfix");
        }
        this.postShowPageHandlerRegistry = [];
        window["krypton"] = window["krypton"] || { module: {} };
    }

    connectedCallback() {
        KryptonElement.mediaQuerySmall.addListener(this.onMediaMobile.bind(this));
        KryptonElement.mediaQueryMedium.addListener(this.onMediaTablet.bind(this));
        KryptonElement.mediaQueryLarge.addListener(this.onMediaDesktop.bind(this));
        super.connectedCallback();
        window.addEventListener("click", this.hideMainMenu.bind(this) as EventListener);
        window.addEventListener('scroll', this.scrollHandler.bind(this) as EventListener)
    }

    scrollHandler(e) {
        if (window["responsive"]?.is?.mobile?.any) {
            let page = this.pageManager?.topPage;
            let elements = [this, this.shadowRoot?.querySelector('header'), page?.querySelector("module-header")?.shadowRoot?.querySelector(".module-header")]
            let newPos: number = window["scrollY"] || window["scrollTop"] || 0
            if (page) {
                page.scrollPosition = newPos;
            }
            let act = (newPos && newPos >= this.prevScrollTop) ? "add" : "remove";
            this.prevScrollTop = newPos;
            elements.forEach(e => { e?.classList[act]("shrink") });
        }
    }

    disconnectedCallback() {
        KryptonElement.mediaQuerySmall.removeListener(this.onMediaMobile.bind(this));
        super.disconnectedCallback();
    }

    hideMainMenu(e) {
        if (this.nav && this.nav.open) {
            this.closeNav(e);
        }
        let moreIconMenu = this.pageManager?.topPage?.querySelector("module-header")?.contextMenu?.menu?.More;
        if (moreIconMenu) {
            moreIconMenu.pin = false;
        }
        moreIconMenu = this.pageManager?.topPage?.querySelector("module-header")?.contextMenu?.menu?.Workspace;
        if (moreIconMenu) {
            moreIconMenu.pin = false;
        }
    }

    closeNav(e) {
        this.nav.open = false;
        this.nav?.shadowRoot?.querySelector("search-menu")?.clear(e as Event);
    }

    preventMainMenuHide(e) {
        e && e.stopPropagation();
    }

    getHomePageUrl(hash) {
        let url = hash ? hash.substring(1) : "/WebDispatch";
        let pos = url.lastIndexOf("#/WebDispatch");
        url = pos > -1 ? url.substring(pos + 1) : url;
        let parts = url.split("?");
        if (parts.length > 1) {
            url = parts.shift() + "?" + parts.join("&");
        }
        return url;
    }

    firstUpdated(changedProperties: PropertyValues) {
        if (!this.disabled) {
            this.loadingCurtain.open = true;
            //-- DETERMINE INITIAL MODULE
            this.loadIinitialModule();

            //-- LOAD MENU
            this.loadMenu();

            //-- LOAD HELP DATA
            (async () => { await KryptonApp.fetchHelpData(); })();

            //-- ENABLE HISTORY
            this.enableHistory();
        }
        super.firstUpdated(changedProperties);

    }

    async loadIinitialModule() {
        const url = this.getHomePageUrl(location.hash);

        const homePage = await this.fetchPage(url);
        homePage.module = "home";
        await this.showPage(homePage, true);

        //-- TODO put this in a polyfill code for replaceState
        if ("pushState" in history) {
            this.replaceHistoryState({ module: "home" }, document.title, UrlResolver.RELATIVE.resolve(url));
        } else {
            let scrollV, scrollH;
            //-- Prevent scrolling by storing the page's current scroll offset
            scrollV = document.body.scrollTop;
            scrollH = document.body.scrollLeft;
            location.hash = "";
            //-- Restore the scroll offset, should be flicker free
            document.body.scrollTop = scrollV;
            document.body.scrollLeft = scrollH;
        }
    }
    async loadMenu() {
        const menuItems: Menu = await KryptonApp.fetchMenu();
        for (let menuItem of menuItems) {
            menuItem.setAttribute("slot", "nav");
            //this.nav.appendChild(menuItem);
            this.appendChild(menuItem);
        }
        this.addEventListener(KryptonEventDetail.MENU_ITEM_EVENT, ((e: CustomEvent) => {
            let kryptonEventDetail: KryptonEventDetail = e.detail;
            if (kryptonEventDetail.isMenuItemClick()) {
                this.handleMenuClick(kryptonEventDetail);
                this.closeNav(e);
            }
        }) as EventListener);
        this.addEventListener(KryptonEventDetail.SHOW_PAGE_COMPLETED_EVENT, ((e: CustomEvent) => {
            let kryptonEventDetail: KryptonEventDetail = e.detail;
            this.handleShowPageEvent(kryptonEventDetail);
        }) as EventListener);
        this.addEventListener(KryptonEventDetail.NOTIFICATION_SHOW_HIDE_EVENT, ((e: CustomEvent) => {
            let kryptonEventDetail: KryptonEventDetail = e.detail;
            this.freezeOrUnfreezeHeader(kryptonEventDetail);
        }) as EventListener);

    }
    async enableHistory() {
        window.onpopstate = e => {

            //if (this.pageManager.pageStackManager.hasPast()) {
            //    this.pageManager.pageStackManager.back();
            //    return;
            //}

            //const module = e.state?.module;
            //const page = e.state?.page || "";

            //if (this.pageManager.size() > 1 && this.pageManager.pageStackManager.hasPast()) {
            //    this.pageManager.pageStackManager.back();
            //    return;
            //}

            //if (module && !page)
            //    this.pageManager.clear();
            //this.openPage(location.href, false);

            //-- ignore hashChange events
            if (e.state === null) {
                e.preventDefault();
                return false;
            }

            (async () => {
                let url = this.getHomePageUrl(location.hash);
                await this.showPage(await this.fetchDebugPage(url), false);
            })();

            return true;
        };

    }
    onMediaMobile(e: MediaQueryListEvent) {
        if (e.matches) {
            let envoyClock = this.shadowRoot?.getElementById("header-clock") as ServerClock;
            envoyClock.shortMonth = true;
        }
    }

    onMediaTablet(e: MediaQueryListEvent) {
        if (e.matches) {
            let envoyClock = this.shadowRoot?.getElementById("header-clock") as ServerClock;
            envoyClock.shortMonth = false;
        }
    }

    onMediaDesktop(e: MediaQueryListEvent) {
        if (e.matches) {
            let envoyClock = this.shadowRoot?.getElementById("header-clock") as ServerClock;
            envoyClock.shortMonth = false;
        }
    }

    onMenuClick(e: CustomEvent) {
        e.stopPropagation();
        if (this.nav.open) {
            this.closeNav(e);
        } else {
            this.nav.open = true;
        }
        if (this.nav.open) {
            let searchText: any = this.nav?.shadowRoot?.querySelector("search-menu")?.shadowRoot?.querySelector("#search-input");
            searchText.focus();
        }
    }

    onPageNavEvent(e: CustomEvent) {
        if (e.detail.name === KryptonEventDetail.PAGE_NAV_EVENT) {
            let kryptonEventDetail: KryptonEventDetail = e.detail;
            let props = kryptonEventDetail.getProperties();
            this.shadowRoot?.getElementById("header-title")?.classList[props.open ? "add" : "remove"]("menu-event");
            this.shadowRoot?.getElementById("header-version")?.classList[props.open ? "add" : "remove"]("menu-event");
            this.shadowRoot?.querySelector("server-clock")?.classList[props.open ? "add" : "remove"]("menu-event");
            this.shadowRoot?.getElementById("menu-icon")?.classList[props.open ? "add" : "remove"]("active");
        }
    }

    onHeaderNavClick(e) {
        e?.preventDefault();
        e?.stopPropagation();
        let targetName = e.target.getAttribute("data-name") || e.target.parentNode?.getAttribute("data-name")
        let closeNav = true;
        switch (targetName) {
            case "Help":
                this._onClickHelp();
                break;
            case "Disclaimer":
                this._onClickDisclaimer();
                break;
            case "Manage Profile":
                this._onClickManageProfile();
                break;
            case "Manage 2FA":
                this._onClickManage2FA();
                break;
            case "Manage Alerts":
                this._onClickManageAlerts();
                break;
            case "Logout":
                this._onClickLogout();
                break;
            default:
                closeNav = false;
                break;
        }

        closeNav && this.closeNav(e);
    }

    _onClickHelp() {
        let helpUrl = this.getHelpUrl();
        console.log("Help URL", helpUrl)
        window.open(helpUrl, '', 'width=770, height=550,scrollbars=yes,resizable=yes');
    }

    getHelpUrl(): string {
        let helpUrl = "";
        let page = this.pageManager.topPage;
        while (page) {
            helpUrl = this.getHelpUrlFromMap(page.action);
            if (helpUrl)
                return helpUrl;
            page = page.opener;
        }

        return "/Public/ViewExternal.showHelp";
    }

    getHelpUrlFromMap(actionUrl): any {
        let url = actionUrl;
        if (actionUrl && actionUrl.indexOf(KryptonConstants.servletName) != -1) {
            url = actionUrl.split(KryptonConstants.servletName + "/")[1];
            url = url.split("?")[0];
        } else {
            url = actionUrl.split("?")[0];
        }

        let helpData = KryptonApp?.data?.help;

        if (helpData)
            return helpData[url];

        return null;
    }

    _onClickManageProfile() {
        openModule('/WebDispatch/Security.showCompleteProfile?' + CSRF.getCsrfToken());
    }

    _onClickManage2FA() {
        openModule('/WebDispatch/Security.getMfaEnrollment?' + CSRF.getCsrfToken());
    }

    _onClickManageAlerts() {
        openModule('/WebDispatch/ViewAlert.getAlertProfile?' + CSRF.getCsrfToken());
    }

    _onClickDisclaimer() {
        openOverlay("/help/disclaimer_message.html");
    }

    _onClickLogout() {
        window['userClickedLogout'] = true;
        window.location.href = '/WebDispatch/Security.logout?showLogoutComplete';
        return true;
    }

    // When user clicks on a menu-item.  Both hamburger menu and menu under more options, filter, print, export all are handled here
    handleMenuClick(kryptonEventDetail: KryptonEventDetail) {
        this.loadingCurtain.open = true;
        this.resetPin();
        let menuEventProps = kryptonEventDetail.getProperties();
        // If the more option is a javascript, trigger that method
        if (menuEventProps?.action?.indexOf("javascript:") >= 0) {
            let jsMethod = menuEventProps.action.trim();
            //TODO:- Sree - eval is not recommended.  The following is a tweak for now.  Any other options??
            Function('return ' + jsMethod.substring(11))();
            return;
        }
        // We are going to show a new page, so if this is a click from main menu, existing pages will need to be removed
        let isSinglePage = !menuEventProps.multiplePages;
        if (isSinglePage) {
            this.pageManager.clear();
        }
        (async () => await this.showPage(await this.fetchDebugPage(menuEventProps.action), isSinglePage, !isSinglePage))();
    }

    resetPin() {
        let contextMenu = this.pageManager?.topPage?.querySelector("module-header")?.contextMenu;
        if (contextMenu) {
            let element = contextMenu.menu?.Workspace;
            if (element) {
                element.pin = false;
            }
            element = contextMenu.menu?.More;
            if (element) {
                element.pin = false;
            }
        }
    }

    static async fetchMenuData(): Promise<any> {
        if (!KryptonApp.data.menu) {
            const response: Response = await fetch(KryptonApp.url.menu + "?" + CSRF.getCsrfToken());
            KryptonApp.data.menu = await response.json();
        }
        return KryptonApp.data.menu;
    }

    static async fetchMenu(): Promise<Menu> {

        let menuJson = await KryptonApp.fetchMenuData() as MenuJson;

        const menuObjectList: any = menuJson.menuObjectList
            , menu: Menu = new Menu();

        menuObjectList.forEach(data => {
            if (data.folder) {
                this.addFolderAsMenu(data?.folder?.folderList, menu);
                return;
            }
            menu.add(new MenuItem(data, data.dropdown && data.dropdown.childMenuList));
        });
        return menu;
    }

    static addFolderAsMenu(folder: any, menu: Menu) {
        if (folder) {
            folder.forEach(element => {
                let menuData: MenuItemJsonData = new MenuItemJsonData();
                menuData.label = element.folderTitle;
                menuData.activation = {
                    actionOnClick: ''
                };
                menuData.label = element.folderTitle;
                menuData.dropdown = {
                    menuGroup: element.folderTitle,
                    childMenuList: element.folderList
                };
                menu.add(new MenuItem(menuData, menuData.dropdown && menuData.dropdown.childMenuList));
            });
        }
    }

    static async fetchHelpData(): Promise<any> {
        if (!KryptonApp.data.help) {
            const response: Response = await fetch(KryptonApp.url.help + "?" + CSRF.getCsrfToken());
            KryptonApp.data.help = await response.json();
        }
        return KryptonApp.data.help;
    }

    static async hasContextMenu(path) {

        if (!path) {
            return false;
        }

        path = path.replace(/^\/WebDispatch\//, "");
        path = path.replace(/\./g, "_");
        if (path.indexOf('?') > 0) {
            path = path.substring(0, path.indexOf('?'))
        }
        const menuJson = await KryptonApp.fetchMenuData()
            , contextMenuDataMap = menuJson["contextMenuDataMap"]
            , contextMenuDataMapAction = contextMenuDataMap[path];
        console.log("hasContextMenu", path, !!contextMenuDataMapAction);
        return !!contextMenuDataMapAction;
    }

    async fetchContextMenu(path): Promise<ContextMenu | null> {

        const contextMenu = new ContextMenu();

        if (!path) {
            return contextMenu;
        }

        path = path.replace(/^\/WebDispatch\//, "");
        path = path.replace(/\./g, "_");
        if (path.indexOf('?') > 0) {
            path = path.substring(0, path.indexOf('?'))
        }
        const menuJson = await KryptonApp.fetchMenuData()
            , workspaceMenuDataMap = menuJson["workspace"]
            , contextMenuDataMap = menuJson["contextMenuDataMap"]
            , contextMenuDataMapAction = contextMenuDataMap[path];

        if (workspaceMenuDataMap) {
            for (var key in workspaceMenuDataMap) {
                if (workspaceMenuDataMap.hasOwnProperty(key)) {
                    contextMenu.addWorkspaceMenu(KryptonApp.makeWorkspaceMenu(workspaceMenuDataMap[key] as WorkspaceJsonData));
                }
            }
        }

        if (!contextMenuDataMapAction) {
            return contextMenu;
        }

        const contextMenuDataList = contextMenuDataMapAction["contextMenuDataList"]; //TODO reference krypton-base-nav to see what it's doing for Ledger/Filter

        if (!contextMenuDataList) {
            return contextMenu;
        }

        contextMenuDataList.forEach(data => contextMenu.add(new MenuContext(data as MenuContextJsonData)));
        return contextMenu;
    }

    static makeWorkspaceMenu(data: WorkspaceJsonData): MenuItem {
        const menuData = new MenuItemJsonData();

        if (data) {
            menuData.description = data.widgetName;
            menuData.label = data.widgetName;
            menuData.activation = {
                actionOnClick: data.applicationUrl
            };
        }

        return new WorkspaceMenu(menuData);
    }

    async refreshWorkspaceMenu(dataArray: any) {
        const menuJson = await KryptonApp.fetchMenuData()
        let workspaceMenuDataMap = {}
        dataArray.forEach(element => {
            workspaceMenuDataMap[element.displayOrder] = element;
        });
        menuJson["workspace"] = workspaceMenuDataMap
    }

    getResolvedUrlForMenu(url: string): any {
        let newUrl = UrlResolver.RELATIVE.resolve(url);
        if (newUrl.indexOf('?') > 0) {
            newUrl = newUrl.substring(0, newUrl.indexOf('?'));
        }
        return newUrl;
    }

    createNotification(message: string, type: NotificationType = "unknown") {
        const notification = new NotificationElement();
        notification.type = type;
        notification.add(message);
        notification.setAttribute("slot", "notification");
        this.appendChild(notification);
    }

    registerHandlerForPostDisplayPageAction(details: any) {
        this.postShowPageHandlerRegistry.push(details);
    }

    showErrorNotification(status) {
        let errpage = status === 401 || status === 404 ? "/error/async/" + status + ".html" : "/error/async/index.html";
        openModule(errpage);
        this.loadingCurtain.open = false;
    }

    async fetchPage(url: string, opener: PageElement = this.pageManager.topPage as PageElement): Promise<PageElement> {
        let fetchUrl = UrlResolver.RELATIVE.resolve(url);
        const response = await fetch(fetchUrl)
        let responseText = "";
        if (response.ok) {
            responseText = await response.text()
        } else {
            this.showErrorNotification(response.status);
        }
        const page = await this.getNewPage(fetchUrl, responseText, opener);
        return page;
    }

    async fetchDebugPage(action: string = this.action) {
        const page = await this.fetchPage(action);
        if (this.debugPage === "yes") {
            const debugInfo = new DebugInfo();
            page.appendChild(debugInfo);
        }
        return page;
    }

    async getNewPage(fetchUrl: string, responseText: any, opener: PageElement) {
        const page = new PageElement(fetchUrl, responseText, opener);
        this.setLoadingPage(page);
        return page
    }

    async showPage(page: PageElement, record: boolean = true, multiplePages: boolean = false) {
        this.loadingCurtain.open = true;
        const oldPage: PageElement = this.pageManager.topPage as PageElement;
        page.setAttribute("slot", "page");
        if (!KryptonConstants.allowMultiplePages && !multiplePages) {
            oldPage?.parentElement?.removeChild(oldPage);
        } else {
            oldPage?.doClose();
            page.opener = oldPage;
        }

        this.appendChild(page);
        page.applyScriptsAndStyles();
        page.open = true;
        if (record) {
            this.pushToHistory(page);
        }
        return oldPage;
    }

    setLoadingPage(loadingPage: PageElement | undefined) {
        this._loadingPage = loadingPage;
    }

    getUrlForHistory(url) {
        let currentUrl = window.location.toString();
        let pos = currentUrl.indexOf("?");
        if (pos > -1) {
            currentUrl = currentUrl.substring(0, pos);
        }
        let formattedUrl = UrlResolver.stripCsrfToken(UrlResolver.RELATIVE.resolve(url));
        let newUrl = UrlResolver.RELATIVE.resolve(currentUrl) + "&page=" + formattedUrl + "#" + formattedUrl;
        return newUrl;
    }

    pushToHistory(page: PageElement) {
        if (page.opener || page.isPageCreatedFromAFormSubmit) {
            return;
        }
        let newUrl = this.getUrlForHistory(page.action);
        history.pushState({ module: page.module, page: page.page }, page.module, newUrl);
    }

    replaceHistoryState(data, title, url) {
        let newUrl = this.getUrlForHistory(url);
        history.replaceState(data, title, newUrl);
    }

    showOrHideFooter(show: boolean) {
        this.shadowRoot?.querySelector("footer")?.classList[show ? "remove" : "add"]("d-none");
    }

    async openPage(url: string, record: boolean = true): Promise<PageElement> {
        let newPage = await this.fetchPage(url);
        return await this.showPage(newPage, record);
    }
    async openKryptonDialog(options: any) {
        let kryptonDialog: KryptonDialog | null | undefined = this.shadowRoot?.querySelector("krypton-dialog");
        kryptonDialog && kryptonDialog.classList && kryptonDialog.classList.remove("d-none");
        kryptonDialog && kryptonDialog.showModal(options);
    }
    // This method is triggered after the page is fully rendered and shown in the UI.  We can turn off the loading curtain and also handle any scripts
    // that should be triggered only after the page is shown.  Scripts that determines height/width of elements may need to be handled here as these
    // values will be zero if not shown on screen
    handleShowPageEvent(kryptonEventDetail: KryptonEventDetail) {
        //let eventDetail: any = kryptonEventDetail.getProperties();
        //if (kryptonEventDetail.getName() == KryptonEventDetail.SHOW_PAGE_COMPLETED_EVENT
        // && kryptonEventDetail.getType() === KryptonEventDetail.SHOW) {
        //this.processShowPageHandler();
        //let page = eventDetail.target;
        //this.loadingCurtain.open = (page && page.querySelector("message-ticker")) ? true : false;
        //}
        if (!window["responsive"]?.is?.mobile?.any) {
            window["resetPageScroll"] && window["resetPageScroll"]();
        }
    }

    freezeOrUnfreezeHeader(kryptonEventDetail) {
        let eventDetail: any = kryptonEventDetail.getProperties();
        if (kryptonEventDetail.getName() == KryptonEventDetail.NOTIFICATION_SHOW_HIDE_EVENT) {
            this.disabled = kryptonEventDetail.getType() === KryptonEventDetail.SHOW;
            if (kryptonEventDetail.getType() === KryptonEventDetail.SHOW) {
                this.loadingCurtain.open = false;
            }
        }
    }

    processShowPageHandler() {
        let handlerProps: any;
        while (handlerProps = this.postShowPageHandlerRegistry.shift()) {
            try {
                console.log("Executing " + handlerProps.description);
                handlerProps.handler(...handlerProps.args);
            } catch (err) {
                console.error("Failed handler " + handlerProps.description);
                console.error(err);
            }
        }
    }

    static styles =
        KryptonAppStyles.stylesApp.concat(
            KryptonAppStyles.stylesHeader.concat(
                KryptonAppStyles.stylesFooter
            ));

    render() {
        return html`
        <page-nav id="nav" @page-nav-event="${this.onPageNavEvent}" @click="${this.preventMainMenuHide}">
            <slot name="nav"></slot>
            <nav id="header-mobile-nav" class="header-hidden-md-up" @click="${this.onHeaderNavClick}">
                <li><a data-name="Help">Help</a></li>
                <li><a data-name="Disclaimer">Disclaimer</a></li>
                <li><a data-name="Manage Profile">Manage Profile</a></li>
                <li><a data-name="Manage 2FA">Manage 2FA</a></li>
                <li><a data-name="Manage Alerts">Manage Alerts</a></li>
                <li><a data-name="Logout">Logout</a></li>
            </nav>
        </page-nav>

        <header id="header" class="${this.disabled ? "disabled" : ""}">
            <span id="menu-icon" @click="${this.onMenuClick}" class="${this.disabled ? "disabled" : ""}">${ICONS.menu.html}</span>
            <h1 id="header-title" @click="${() => openModule('/WebDispatch/ViewLandingPage.getLandingPage')}"><span class="company-name">SoCalGas</span> <span>ENVOY</span></h1>
            <app-version id="header-version" version="${this.version}" environment="${this.environment}" branch="${this.branch}"></app-version>
            <div id="header-nav-container">
                <server-clock id="header-clock"></server-clock>
                <nav id="header-nav" @click="${this.onHeaderNavClick}">
                    <a data-name="Manage Alerts"><span class="header-hidden-md">Manage&nbsp;</span>Alerts</a>
                    <a data-name="Manage Profile"><span class="header-hidden-md">Manage&nbsp;</span>Profile</a>
                    <a data-name="Manage 2FA"><span class="header-hidden-md">Manage&nbsp;</span>2FA</a>
                    <a data-name="Disclaimer">Disclaimer</a>
                    <a data-name="Help">Help</a>
                    <a data-name="Logout">Logout</a>
                </nav>
            </div>
        </header>

        <page-manager id="page-manager" role="main" open>
            <slot name="page"></slot>
        </page-manager>
        <div style="height: 50px">&nbsp;
        </div>
        <footer id="footer">
            <div id="footer-header" @click="${() => this.shadowRoot?.getElementById("footer")?.classList.toggle("mobile-open")}">SoCalGas ENVOY<sup>&reg;</sup></div>
            <div id="footer-body">
                <div>Use of this web site and SoCalGas ENVOY<sup>&reg;</sup> is subject to Company Legal Terms and Conditions and Privacy, which are incorporated by reference herein.</div>
                <div>SoCalGas ENVOY<sup>&reg;</sup> is also governed by the current Tariff Rules applicable to such program filed with the CPUC.</div>
                <div><span class="text-nowrap">&copy; ${KryptonApp.data.copyrightDate.start}-${KryptonApp.data.copyrightDate.end} Southern California Gas Company.</span> The trademarks used herein are the property of their respective owners. All rights reserved.</div>
            </div>
        </footer>

        <notification-board id="notification-board" opened>
            <slot name="notification"></slot>
        </notification-board>

        <loading-curtain id="loading-curtain" global ?opened=${false}></loading-curtain>
        <krypton-dialog class="d-none"></krypton-dialog>
        `;
    }

    getParentPageElement(node: Node) {
        let parentElement = node.parentElement;
        while (parentElement && !(parentElement instanceof PageElement))
            parentElement = parentElement.parentElement;
        return parentElement;
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'krypton-app': KryptonApp;
    }
}
